import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { type TaskKind, type TaskStatus, type TaskEntityType } from "@ckgo2/shared/types";
import { Circle, Sparkles, CalendarDays, AlertCircle, Wand2, ChevronRight } from "lucide-react";
import { useAgent } from "@/components/agent/AgentProvider";

interface ProactiveSuggestion {
  id: string;
  kind: "payment_reminder" | "departure_soon" | "guide_unstaffed";
  severity: "high" | "medium";
  title: string;
  detail: string;
  prompt: string;
  entityType: "order" | "departure";
  entityId: string;
  orderNumber?: string;
}

interface TaskRow {
  id: string;
  entityType: TaskEntityType;
  entityId: string;
  kind: TaskKind;
  title: string;
  dueDate?: string | null;
  status: TaskStatus;
  orderNumber?: string | null;
}

interface MyDayResponse {
  data: { overdue: TaskRow[]; today: TaskRow[]; upcoming: TaskRow[]; noDue: TaskRow[] };
  counts: { overdue: number; today: number; upcoming: number; open: number };
  success: true;
}

interface Briefing {
  text: string;
  stats: { overdue: number; today: number; upcoming: number; unread: number };
}

function entityLink(t: TaskRow): string | null {
  if (t.entityType === "order") return `/orders/${t.entityId}`;
  if (t.entityType === "lead") return `/leads/${t.entityId}`;
  return null;
}

/**
 * "My Day" (vision §5.15) — finishes the Block-1-deferred aggregator. The current user's
 * open tasks bucketed overdue/today/upcoming, fronted by an AI morning briefing (B2.2).
 */
export default function MyDayPanel() {
  const qc = useQueryClient();
  const { enabled: agentEnabled, openChat } = useAgent();

  const { data, isLoading } = useQuery({
    queryKey: ["my-day"],
    queryFn: () => api.get<MyDayResponse>("/api/admin/tasks/my-day"),
  });
  const { data: briefingData } = useQuery({
    queryKey: ["my-day-briefing"],
    queryFn: () => api.get<ApiResponse<Briefing>>("/api/admin/tasks/briefing"),
    staleTime: 10 * 60 * 1000,
  });
  const { data: suggestData } = useQuery({
    queryKey: ["my-day-suggestions"],
    queryFn: () =>
      api.get<ApiResponse<{ enabled: boolean; suggestions: ProactiveSuggestion[] }>>(
        "/api/admin/agent/suggestions",
      ),
    staleTime: 5 * 60 * 1000,
  });
  const suggestions = suggestData?.data.suggestions ?? [];

  const toggle = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.patch(`/api/admin/tasks/${id}/status`, { status: done ? "done" : "open" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-day"] });
      qc.invalidateQueries({ queryKey: ["my-day-briefing"] });
    },
  });

  const buckets = data?.data;
  const counts = data?.counts;
  const briefing = briefingData?.data;
  const hasAny =
    !!buckets &&
    buckets.overdue.length + buckets.today.length + buckets.upcoming.length + buckets.noDue.length >
      0;

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Sparkles className="text-primary-500 h-5 w-5" /> My Day
        </h2>
        <Link
          to="/calendar"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 text-sm font-medium"
        >
          <CalendarDays className="h-4 w-4" /> Calendar
        </Link>
      </div>

      {briefing?.text && (
        <div className="bg-primary-50 dark:bg-primary-900/20 mb-4 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-200">
          {briefing.text}
        </div>
      )}

      {agentEnabled && suggestions.length > 0 && (
        <div className="mb-4 space-y-2">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">
            <Wand2 className="h-3.5 w-3.5" /> Suggested by MAKEJ!
          </h3>
          <ul className="space-y-1.5">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => openChat(s.prompt)}
                  className="group flex w-full items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-left transition-colors hover:bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/10 dark:hover:bg-amber-900/20"
                  title="Hand this to MAKEJ! to draft"
                >
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      s.severity === "high" ? "bg-red-500" : "bg-amber-400"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      {s.title}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {s.detail}
                    </span>
                  </span>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : !hasAny ? (
        <p className="py-6 text-center text-sm text-gray-400">
          No open tasks — you're all caught up 🎉
        </p>
      ) : (
        <div className="space-y-4">
          {buckets!.overdue.length > 0 && (
            <Bucket
              title="Overdue"
              tone="danger"
              count={counts?.overdue}
              tasks={buckets!.overdue}
              toggle={toggle}
            />
          )}
          {buckets!.today.length > 0 && (
            <Bucket
              title="Today"
              tone="today"
              count={counts?.today}
              tasks={buckets!.today}
              toggle={toggle}
            />
          )}
          {buckets!.upcoming.length > 0 && (
            <Bucket
              title="This week"
              tone="muted"
              count={counts?.upcoming}
              tasks={buckets!.upcoming}
              toggle={toggle}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Bucket({
  title,
  tone,
  count,
  tasks,
  toggle,
}: {
  title: string;
  tone: "danger" | "today" | "muted";
  count?: number;
  tasks: TaskRow[];
  toggle: ReturnType<typeof useMutation<unknown, Error, { id: string; done: boolean }>>;
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-600 dark:text-red-400"
      : tone === "today"
        ? "text-primary-600 dark:text-primary-400"
        : "text-gray-500";
  return (
    <div>
      <h3
        className={`mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase ${toneClass}`}
      >
        {tone === "danger" && <AlertCircle className="h-3.5 w-3.5" />}
        {title} {count != null && `(${count})`}
      </h3>
      <ul className="space-y-1.5">
        {tasks.map((t) => {
          const link = entityLink(t);
          return (
            <li key={t.id} className="flex items-start gap-2">
              <button
                onClick={() => toggle.mutate({ id: t.id, done: true })}
                className="mt-0.5"
                title="Mark done"
              >
                <Circle className="h-4 w-4 text-gray-400 hover:text-emerald-500" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 dark:text-gray-200">{t.title}</p>
                {link && t.orderNumber && (
                  <Link
                    to={link}
                    className="text-primary-600 dark:text-primary-400 font-mono text-xs"
                  >
                    {t.orderNumber}
                  </Link>
                )}
              </div>
              {t.dueDate && (
                <span className="shrink-0 text-xs text-gray-400">{t.dueDate.slice(5, 10)}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
